import { useRef, useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Bold, Italic, List, ListOrdered, Heading1, Heading2, Heading3, Undo, Redo, Paintbrush, Pilcrow } from 'lucide-react';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  className?: string;
  minHeight?: string;
  maxHeight?: string;
}

interface CopiedFormat {
  bold: boolean;
  italic: boolean;
  fontName: string | null;
  fontSize: string | null;
}

const FONT_SIZES = [
  { value: '1', label: 'Klein' },
  { value: '2', label: 'Normaal' },
  { value: '3', label: 'Medium' },
  { value: '4', label: 'Groot' },
  { value: '5', label: 'Groter' },
  { value: '6', label: 'XL' },
  { value: '7', label: 'XXL' },
];

const FONT_FAMILIES = [
  { value: 'Arial', label: 'Arial' },
  { value: 'Verdana', label: 'Verdana' },
  { value: 'Helvetica', label: 'Helvetica' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Times New Roman', label: 'Times New Roman' },
  { value: 'Courier New', label: 'Courier New' },
];

export function RichTextEditor({ 
  value, 
  onChange, 
  onBlur,
  placeholder = 'Typ hier...',
  className,
  minHeight = '200px',
  maxHeight = '500px'
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const isInternalUpdate = useRef(false);
  const [copiedFormat, setCopiedFormat] = useState<CopiedFormat | null>(null);

  // Initialize and sync content
  useEffect(() => {
    if (editorRef.current && !isInternalUpdate.current) {
      // Only update if content is actually different
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
    isInternalUpdate.current = false;
  }, [value]);

  // Handle paste from Word - clean up messy HTML
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    
    let html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    
    if (html) {
      // Clean up Word/Office HTML
      html = cleanWordHtml(html);
      document.execCommand('insertHTML', false, html);
    } else {
      // Plain text - preserve line breaks
      const formattedText = text
        .split('\n\n')
        .map(para => `<p>${para.replace(/\n/g, '<br>')}</p>`)
        .join('');
      document.execCommand('insertHTML', false, formattedText);
    }
    
    handleInput();
  }, []);

  const cleanWordHtml = (html: string): string => {
    // Remove Word-specific tags and attributes
    let cleaned = html
      // Remove entire <style> blocks (Word Style Definitions)
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
      // Remove HTML comments (including Word conditional comments)
      .replace(/<!--[\s\S]*?-->/gi, '')
      // Remove XML declarations
      .replace(/<\?xml[^>]*>/gi, '')
      // Remove MS Office tags
      .replace(/<o:[^>]*>/gi, '')
      .replace(/<\/o:[^>]*>/gi, '')
      .replace(/<w:[^>]*>/gi, '')
      .replace(/<\/w:[^>]*>/gi, '')
      .replace(/<m:[^>]*>/gi, '')
      .replace(/<\/m:[^>]*>/gi, '')
      // Remove class attributes
      .replace(/class="[^"]*"/gi, '')
      // Remove style attributes (keep some basic ones)
      .replace(/style="[^"]*"/gi, '')
      // Remove MS Office namespaces
      .replace(/xmlns:[^=]*="[^"]*"/gi, '')
      // Remove mso-* inline style rules that may have survived
      .replace(/mso-[^;:"']+:[^;:"']+;?/gi, '')
      // Remove table.MsoNormalTable and similar CSS blocks
      .replace(/table\.Mso[^{]+\{[^}]+\}/gi, '')
      // Remove Style Definitions comment blocks
      .replace(/\/\*\s*Style Definitions[^*]*\*\//gi, '')
      // Remove {mso-...} CSS-like blocks
      .replace(/\{mso-[^}]+\}/gi, '')
      // Remove empty spans
      .replace(/<span[^>]*>\s*<\/span>/gi, '')
      // Remove font tags
      .replace(/<font[^>]*>/gi, '')
      .replace(/<\/font>/gi, '')
      // Clean up whitespace
      .replace(/\s+/g, ' ')
      // Remove empty paragraphs
      .replace(/<p[^>]*>\s*<\/p>/gi, '')
      // Convert Word bullets to proper lists
      .replace(/·\s*/g, '')
      // Normalize spacing around tags
      .replace(/>\s+</g, '><')
      .trim();
    
    return cleaned;
  };

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      isInternalUpdate.current = true;
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const handleBlur = useCallback(() => {
    if (onBlur) {
      onBlur();
    }
  }, [onBlur]);

  // Normalize Enter key behavior
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Normalize Enter behavior to create new paragraphs
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      document.execCommand('insertParagraph', false);
      handleInput();
      return;
    }
    
    // Shift+Enter for line break within paragraph
    if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();
      document.execCommand('insertLineBreak', false);
      handleInput();
      return;
    }
  }, [handleInput]);

  const execCommand = useCallback((command: string, value: string | null = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  }, [handleInput]);

  const formatBlock = useCallback((tag: string) => {
    document.execCommand('formatBlock', false, tag);
    editorRef.current?.focus();
    handleInput();
  }, [handleInput]);

  // Format Painter: Copy format from selection
  const copyFormat = useCallback(() => {
    const bold = document.queryCommandState('bold');
    const italic = document.queryCommandState('italic');
    const fontName = document.queryCommandValue('fontName') || null;
    const fontSize = document.queryCommandValue('fontSize') || null;
    
    setCopiedFormat({ bold, italic, fontName, fontSize });
    toast.info('Opmaak gekopieerd - selecteer tekst om toe te passen');
  }, []);

  // Format Painter: Apply copied format to selection
  const applyFormat = useCallback(() => {
    if (!copiedFormat) return;
    
    // First remove existing formatting
    document.execCommand('removeFormat', false);
    
    // Apply copied formatting
    if (copiedFormat.bold) {
      document.execCommand('bold', false);
    }
    if (copiedFormat.italic) {
      document.execCommand('italic', false);
    }
    if (copiedFormat.fontName) {
      document.execCommand('fontName', false, copiedFormat.fontName);
    }
    if (copiedFormat.fontSize) {
      document.execCommand('fontSize', false, copiedFormat.fontSize);
    }
    
    editorRef.current?.focus();
    handleInput();
    setCopiedFormat(null);
    toast.success('Opmaak toegepast');
  }, [copiedFormat, handleInput]);

  // Handle format painter click
  const handleFormatPainterClick = useCallback(() => {
    if (copiedFormat) {
      applyFormat();
    } else {
      copyFormat();
    }
  }, [copiedFormat, applyFormat, copyFormat]);

  const ToolbarButton = ({ 
    onClick, 
    icon: Icon, 
    title,
    active = false 
  }: { 
    onClick: () => void; 
    icon: React.ElementType; 
    title: string;
    active?: boolean;
  }) => (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className={cn("h-8 w-8 p-0", active && "bg-muted")}
      onClick={onClick}
      title={title}
    >
      <Icon className="h-4 w-4" />
    </Button>
  );

  return (
    <div className={cn("border rounded-lg bg-background flex flex-col", className)}>
      {/* Toolbar - STICKY so it stays visible when scrolling long content */}
      <div className="sticky top-0 z-10 flex items-center gap-1 p-2 border-b bg-muted/50 backdrop-blur-sm flex-wrap">
        <ToolbarButton 
          onClick={() => execCommand('bold')} 
          icon={Bold} 
          title="Vetgedrukt (Ctrl+B)"
        />
        <ToolbarButton 
          onClick={() => execCommand('italic')} 
          icon={Italic} 
          title="Cursief (Ctrl+I)"
        />
        
        <div className="w-px h-6 bg-border mx-1" />
        
        <ToolbarButton 
          onClick={() => formatBlock('p')} 
          icon={Pilcrow} 
          title="Normale tekst (10pt) - Standaard paragrafen"
        />
        <ToolbarButton 
          onClick={() => formatBlock('h1')} 
          icon={Heading1} 
          title="Sectietitel (14pt, groen, vet) - Hoofdtitels"
        />
        <ToolbarButton 
          onClick={() => formatBlock('h2')} 
          icon={Heading2} 
          title="Subtitel (12pt, groen balk) - Gebruik spaarzaam"
        />
        <ToolbarButton 
          onClick={() => formatBlock('h3')} 
          icon={Heading3} 
          title="Subkop (11pt, vet) - Onderverdeling"
        />
        
        <div className="w-px h-6 bg-border mx-1" />
        
        <ToolbarButton 
          onClick={() => execCommand('insertUnorderedList')} 
          icon={List} 
          title="Opsomming"
        />
        <ToolbarButton 
          onClick={() => execCommand('insertOrderedList')} 
          icon={ListOrdered} 
          title="Genummerde lijst"
        />
        
        <div className="w-px h-6 bg-border mx-1" />
        
        <ToolbarButton 
          onClick={() => execCommand('undo')} 
          icon={Undo} 
          title="Ongedaan maken (Ctrl+Z)"
        />
        <ToolbarButton 
          onClick={() => execCommand('redo')} 
          icon={Redo} 
          title="Opnieuw (Ctrl+Y)"
        />
        
        <div className="w-px h-6 bg-border mx-1" />
        
        {/* Font Size Dropdown */}
        <Select onValueChange={(val) => execCommand('fontSize', val)}>
          <SelectTrigger className="h-8 w-[80px] text-xs bg-background">
            <SelectValue placeholder="Grootte" />
          </SelectTrigger>
          <SelectContent className="bg-background z-50">
            {FONT_SIZES.map((size) => (
              <SelectItem key={size.value} value={size.value}>
                {size.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {/* Font Family Dropdown */}
        <Select onValueChange={(val) => execCommand('fontName', val)}>
          <SelectTrigger className="h-8 w-[100px] text-xs bg-background">
            <SelectValue placeholder="Lettertype" />
          </SelectTrigger>
          <SelectContent className="bg-background z-50">
            {FONT_FAMILIES.map((font) => (
              <SelectItem 
                key={font.value} 
                value={font.value}
                style={{ fontFamily: font.value }}
              >
                {font.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <div className="w-px h-6 bg-border mx-1" />
        
        {/* Format Painter Button */}
        <ToolbarButton 
          onClick={handleFormatPainterClick}
          icon={Paintbrush} 
          title={copiedFormat ? "Klik om opmaak toe te passen" : "Kopieer opmaak"}
          active={!!copiedFormat}
        />
        
        <span className="ml-auto text-xs text-muted-foreground hidden sm:inline">
          ¶=Tekst • H1=Titel • H2=Subtitel
        </span>
        <span className="text-xs text-muted-foreground sm:hidden">
          {copiedFormat ? '🎨 Opmaak klaar' : ''}
        </span>
      </div>
      
      {/* Scrollable editor area with CLEAR visual markers for heading types */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onBlur={handleBlur}
        onPaste={handlePaste}
        onKeyDown={handleKeyDown}
        className={cn(
          "p-4 outline-none prose prose-sm max-w-none focus:ring-0 overflow-y-auto",
          // Typography with clear visual hierarchy
          "prose-headings:font-bold prose-headings:tracking-tight",
          
          // ===== H1: Most prominent - Green background bar with H1 badge =====
          "prose-h1:text-xl prose-h1:leading-snug prose-h1:mt-6 prose-h1:mb-4",
          "prose-h1:text-primary prose-h1:font-bold",
          "prose-h1:bg-primary/10 prose-h1:border-l-4 prose-h1:border-primary",
          "prose-h1:px-3 prose-h1:py-2 prose-h1:rounded-r",
          // H1 badge using ::before
          "[&_h1]:relative [&_h1]:flex [&_h1]:items-center [&_h1]:gap-2",
          "[&_h1]:before:content-['H1'] [&_h1]:before:text-[10px] [&_h1]:before:font-medium",
          "[&_h1]:before:bg-primary [&_h1]:before:text-primary-foreground",
          "[&_h1]:before:px-1.5 [&_h1]:before:py-0.5 [&_h1]:before:rounded [&_h1]:before:shrink-0",
          
          // ===== H2: Medium prominence - Left accent with lighter background =====
          "prose-h2:text-lg prose-h2:leading-snug prose-h2:mt-5 prose-h2:mb-3",
          "prose-h2:text-primary prose-h2:font-semibold",
          "prose-h2:bg-primary/5 prose-h2:border-l-4 prose-h2:border-primary/70",
          "prose-h2:pl-3 prose-h2:py-1.5 prose-h2:rounded-r",
          // H2 badge
          "[&_h2]:relative [&_h2]:flex [&_h2]:items-center [&_h2]:gap-2",
          "[&_h2]:before:content-['H2'] [&_h2]:before:text-[10px] [&_h2]:before:font-medium",
          "[&_h2]:before:bg-primary/70 [&_h2]:before:text-primary-foreground",
          "[&_h2]:before:px-1.5 [&_h2]:before:py-0.5 [&_h2]:before:rounded [&_h2]:before:shrink-0",
          
          // ===== H3: Subtle - Dashed underline with muted badge =====
          "prose-h3:text-base prose-h3:leading-snug prose-h3:mt-4 prose-h3:mb-2",
          "prose-h3:text-foreground prose-h3:font-semibold",
          "prose-h3:border-b-2 prose-h3:border-dashed prose-h3:border-primary/40 prose-h3:pb-1.5",
          // H3 badge  
          "[&_h3]:relative [&_h3]:flex [&_h3]:items-center [&_h3]:gap-2",
          "[&_h3]:before:content-['H3'] [&_h3]:before:text-[10px] [&_h3]:before:font-medium",
          "[&_h3]:before:bg-muted [&_h3]:before:text-muted-foreground",
          "[&_h3]:before:px-1.5 [&_h3]:before:py-0.5 [&_h3]:before:rounded [&_h3]:before:shrink-0",
          
          // ===== Paragraphs - No decoration, clear baseline =====
          "prose-p:my-2 prose-p:text-foreground prose-p:leading-relaxed prose-p:text-sm",
          
          // Lists with brand color markers
          "prose-ul:my-3 prose-ol:my-3 prose-ul:pl-5 prose-ol:pl-5",
          "prose-li:my-1 prose-li:marker:text-primary prose-li:marker:font-bold prose-li:text-sm",
          // Bold and italic
          "prose-strong:text-foreground prose-strong:font-bold",
          "prose-em:text-foreground prose-em:italic prose-em:text-muted-foreground",
          // Empty placeholder
          "empty:before:content-[attr(data-placeholder)]",
          "empty:before:text-muted-foreground empty:before:pointer-events-none",
          "[&:empty]:before:content-[attr(data-placeholder)]",
          // Format painter cursor when active
          copiedFormat && "cursor-[url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"20\" height=\"20\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\"><path d=\"M4 14h6v6\"/><path d=\"m4 14 14-4-4-4-14 4\"/></svg>'),_crosshair]"
        )}
        style={{ minHeight, maxHeight }}
        data-placeholder={placeholder}
        suppressContentEditableWarning
      />
    </div>
  );
}
